import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { MobXProviderContext } from "mobx-react";
import { getErrorTitle, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES } from "../../../constants/constants";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from 'primereact/toast';
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { convertIsoToDateOnly } from "../../../utils/stringUtils";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Calendar } from "primereact/calendar";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { ToggleButton } from "primereact/togglebutton";
import { InputMask } from "primereact/inputmask";
import { SelectButton } from "primereact/selectbutton";

interface ContactSectionProps {
  uniqueKey?: number;
  sectionData: any;
  enableEditing: boolean;
  submitButtonClick?: (data: any, index?: number) => void;
  deleteButtonClick?: (data: any, index?: number) => void;
  showHelpMessage?: (message: string) => void;
  relationshipReadOnly?: boolean;
}

const contactFieldStyle: React.CSSProperties = {
  minWidth: "260px",
  flex: "1 1 calc(50% - 1rem)",
};

const contactFieldFullWidthStyle: React.CSSProperties = {
  minWidth: "260px",
  flex: "1 1 100%",
};

const contactFieldWrapperStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

const contactReadOnlyCellStyle: React.CSSProperties = {
  minWidth: "220px",
  flex: "1 1 calc(50% - 1rem)",
  border: "1px solid #dbe7f3",
  borderRadius: "14px",
  padding: "0.9rem 1rem",
  background: "#f8fbff",
};

const ContactSection: React.FC<ContactSectionProps> = ({
  uniqueKey, sectionData, enableEditing, submitButtonClick, deleteButtonClick, showHelpMessage, relationshipReadOnly = false
}) => {
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const location = useLocation();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [loadingSection, setLoadingSection] = useState<boolean>(false);
  const [enableEditingState, setEnableEditingState] = useState<boolean>(enableEditing);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    fullName: sectionData?.fullName || "",
    relationship: sectionData?.relationship || "",
    phoneNumber: sectionData?.phoneNumber || "",
    email: sectionData?.email || "",
    faxNumber: sectionData?.faxNumber || "",
    address: sectionData?.address || "",
    preferredContactMethod: sectionData?.preferredContactMethod || "",
  }

  useEffect(() => {
    setEnableEditingState(enableEditing);
  }, [enableEditing]);

  const { control, formState: { errors }, handleSubmit, reset, watch, register } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] ? <small className="p-error" style={{ display: "block" }}>{errors[name].message}</small> : null;
  };

  const onSubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data, uniqueKey);
  }

  const onDelete = (e, data) => {
    e.preventDefault();
    if (deleteButtonClick) deleteButtonClick(data, uniqueKey);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>

      <div className="flex-column flex-wrap ">
        {
          enableEditingState ? (
            <Card className="resident-careplan-card resident-careplan-contact-card" footer={
              <div className="flex justify-content-center gap-2" style={{ width: "auto" }}>
                <Button label="Save" icon="pi pi-check" severity="warning" outlined rounded className="w-auto" style={{ width: "auto" }} onClick={handleSubmit(onSubmit)} />
              </div>
            }>
              <form>
                <div className="flex flex-wrap align-items-start justify-content-center" style={{ gap: "1rem", maxWidth: "920px", margin: "0 auto" }}>
                  <div style={{ ...contactFieldStyle, ...contactFieldWrapperStyle }}>
                    <div className="p-float-label">
                      <Controller name="fullName" control={control} rules={{
                      required: 'Name is required.',
                      maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                      }} render={({ field, fieldState }) => (
                        <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="fullName" className={classNames({ 'p-error': errors.fullName })}>Full Name</label>
                    </div>
                    {getFormErrorMessage('fullName')}
                  </div>
                  <div style={{ ...contactFieldStyle, ...contactFieldWrapperStyle }}>
                    <span className="flex align-items-center gap-2 mb-2">
                      <label htmlFor="relationship" className={classNames({ 'p-error': errors.relationship })}>
                        Relationship
                      </label>
                      <span className="p-float-label" style={{ cursor: 'pointer' }}
                        onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.RELATED_PARTIES_RELATIONSHIP) }}>
                        <i className="pi pi-info-circle"></i>
                      </span>
                    </span>
                    <span className="p-float-label">
                      <Controller name="relationship" control={control} rules={{
                        required: 'Relationship is required.',
                        maxLength: { value: 100, message: 'Relationship must be at most 100 characters long.' }
                      }} render={({ field, fieldState }) => (
                        relationshipReadOnly ? (
                          <div
                            id={field.name}
                            style={{
                              border: "1px solid #dbe7f3",
                              borderRadius: "12px",
                              minHeight: "42px",
                              padding: "0.75rem 0.9rem",
                              background: "#f8fbff",
                              color: "#0f172a",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {field.value || "-"}
                          </div>
                        ) : (
                          <InputText
                            id={field.name}
                            {...field}
                            className={classNames({ 'p-invalid': fieldState.invalid })}
                          />
                        )
                      )} />
                    </span>
                    {getFormErrorMessage('relationship')}
                  </div>
                  <div style={{ ...contactFieldStyle, ...contactFieldWrapperStyle }}>
                    <div className="p-float-label">
                      <Controller name="phoneNumber" control={control} rules={{
                        required: 'Phone number is required.',
                        validate: (value) => {
                          const normalized = String(value || "").trim();
                          if (!normalized) return 'Phone number is required.';
                          if (normalized.includes("_")) return 'Please enter a complete phone number.';
                          return true;
                        },
                      }} render={({ field, fieldState }) => (
                        <InputMask mask="(999) 999-9999" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="phoneNumber" className={classNames({ 'p-error': errors.phoneNumber })}>Phone number</label>
                    </div>
                    {getFormErrorMessage('phoneNumber')}
                  </div>
                  <div style={{ ...contactFieldStyle, ...contactFieldWrapperStyle }}>
                    <div className="p-float-label">
                      <Controller name="faxNumber" control={control} rules={{
                        validate: (value) => {
                          const normalized = String(value || "").trim();
                          if (!normalized) return true;
                          if (normalized.includes("_")) return 'Please enter a complete fax number.';
                          return true;
                        },
                      }} render={({ field, fieldState }) => (
                        <InputMask mask="(999) 999-9999" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="faxNumber" className={classNames({ 'p-error': errors.faxNumber })}>Fax number</label>
                    </div>
                    {getFormErrorMessage('faxNumber')}
                  </div>
                  <div style={{ ...contactFieldStyle, ...contactFieldWrapperStyle }}>
                    <div className="p-float-label">
                      <Controller name="email" control={control} rules={{
                        maxLength: { value: 100, message: 'Email must be at most 100 characters long.' },
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address.',
                        },
                      }} render={({ field, fieldState }) => (
                        <InputText type="email" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="email" className={classNames({ 'p-error': errors.email })}>Email</label>
                    </div>
                    {getFormErrorMessage('email')}
                  </div>
                  <div style={{ ...contactFieldFullWidthStyle, ...contactFieldWrapperStyle }}>
                    <div className="p-float-label">
                      <Controller name="address" control={control} rules={{
                        maxLength: { value: 100, message: 'Address must be at most 100 characters long.' }
                      }} render={({ field, fieldState }) => (
                        <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="address" className={classNames({ 'p-error': errors.address })}>Address</label>
                    </div>
                    {getFormErrorMessage('address')}
                  </div>
                  <div style={{ ...contactFieldFullWidthStyle, ...contactFieldWrapperStyle }}>
                    <label htmlFor="preferredContactMethod" className={classNames({ 'p-error': errors.preferredContactMethod })}>Preferred contact method</label>
                    <div className="p-float-label mt-2">
                      <Controller name="preferredContactMethod" control={control} rules={{
                        maxLength: { value: 100, message: 'Preferred contact method must be at most 100 characters long.' }
                      }} render={({ field, fieldState }) => (
                        <SelectButton id={field.name} {...field} options={['Phone', 'Email', 'Fax']} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                    </div>
                    {getFormErrorMessage('preferredContactMethod')}
                  </div>
                </div>
              </form>
            </Card>
          ) : (
            <Card className="resident-careplan-card resident-careplan-contact-card" footer={
              <div className="flex justify-content-center gap-2">
                <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => { setEnableEditingState(true); }} />
                <Button label="Delete" icon="pi pi-trash" severity="danger" outlined rounded onClick={(e) => onDelete(e, sectionData)} />
              </div>
            }>
              <div className="flex flex-wrap align-items-start" style={{ gap: "1rem" }}>
                <div style={contactReadOnlyCellStyle}><strong>Full name:</strong><br />{sectionData?.fullName || "-"}</div>
                <div style={contactReadOnlyCellStyle}><strong>Relationship:</strong><br />{sectionData?.relationship || "-"}</div>
                <div style={contactReadOnlyCellStyle}><strong>Phone number:</strong><br />{sectionData?.phoneNumber || "-"}</div>
                <div style={contactReadOnlyCellStyle}><strong>Fax number:</strong><br />{sectionData?.faxNumber || "-"}</div>
                <div style={contactReadOnlyCellStyle}><strong>Email:</strong><br />{sectionData?.email || "-"}</div>
                <div style={contactReadOnlyCellStyle}><strong>Preferred contact method:</strong><br />{sectionData?.preferredContactMethod || "-"}</div>
                <div style={{ ...contactReadOnlyCellStyle, flex: "1 1 100%" }}><strong>Address:</strong><br />{sectionData?.address || "-"}</div>
              </div>
            </Card>
          )
        }
      </div>
    </div >
  );
};

export default ContactSection;


